import { useState, useEffect, useCallback } from 'react';
import api from '../api';

const SETUP_ORDER = ['property', 'tenant', 'lease'];

/**
 * Fetches landlord-scoped properties, tenants, and leases to derive onboarding checklist progress.
 * Source of truth for Home complete/incomplete — never overridden by localStorage flags.
 */
export default function useSetupProgress() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [leases, setLeases] = useState([]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [propRes, tenantRes, leaseRes] = await Promise.all([
        api.get('/api/properties'),
        api.get('/api/tenants'),
        api.get('/api/leases'),
      ]);
      setProperties(Array.isArray(propRes.data) ? propRes.data : []);
      setTenants(Array.isArray(tenantRes.data) ? tenantRes.data : []);
      setLeases(Array.isArray(leaseRes.data) ? leaseRes.data : []);
    } catch (err) {
      const message =
        err.response?.data?.error ||
        err.message ||
        'Could not load setup progress';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const stepPropertyDone = properties.some(
    (p) => Array.isArray(p.units) && p.units.length > 0
  );
  const stepTenantDone = tenants.length > 0;
  const stepLeaseDone = leases.length > 0;
  const allComplete = stepPropertyDone && stepTenantDone && stepLeaseDone;
  const completedCount = [stepPropertyDone, stepTenantDone, stepLeaseDone].filter(
    Boolean
  ).length;

  const stepDone = {
    property: stepPropertyDone,
    tenant: stepTenantDone,
    lease: stepLeaseDone,
  };
  const nextStepKey = SETUP_ORDER.find((key) => !stepDone[key]) || null;

  /** A incomplete (0–1) · B partial (2) · C/D complete (3) — D is UI flag only */
  let homePhase = 'incomplete';
  if (allComplete) homePhase = 'complete';
  else if (completedCount === 2) homePhase = 'partial';

  return {
    loading,
    error,
    refresh,
    properties,
    tenants,
    leases,
    steps: {
      property: { done: stepPropertyDone, partial: properties.length > 0 && !stepPropertyDone },
      tenant: { done: stepTenantDone },
      lease: { done: stepLeaseDone },
    },
    allComplete,
    completedCount,
    totalSteps: 3,
    nextStepKey,
    homePhase,
  };
}
